import React, { useMemo } from 'react';
import { riskRatingConfig } from './riskRatingDefaults';
import { scopeTableConfig, findingsSummaryLabels, findingsSummaryColumns } from './tableLabelDefaults';
import { walkthroughColumns, walkthroughLabels, walkthroughInlineUid, walkthroughDescriptionField, walkthroughShowsDescription } from './walkthroughColumns';
import { owaspFindingsConfig } from './owaspFindingsDefaults';
import { revalidationColumns, revalidationLabels, revalidationValue, revalidationRows, REVALIDATION_STATUS_COLORS } from './revalidationDefaults';
import { findingCardConfig, findingCardFindings, findingCardValue, findingCardLayout, findingCardSeverityBg, findingCardParagraphs, reportTwips, findingCardGrid } from './findingCardDefaults';
import { NEXT_STEPS_DEFAULTS, NEXT_STEPS_PANELS, nextStepsColumns } from './nextStepsDefaults';
import { tocEntries } from './tocEntries';
import { metricTileLabelGap, metricTileAlign } from './metricTileDefaults';
import { normaliseAuthoredLists } from './authoredHtml';
import { cssFontFor } from './fontOptions';
import {
  resolveHeaderFooterLayout, isEmptyPart, headerFooterZoneWidths,
  headerFooterTextLines, headerFooterLinkColor,
} from './headerFooterDefaults';

const SAMPLE_FINDINGS = [
  { id: 1, title: 'SQL Injection in Login Form', severity: 'Critical', cvss_score: 9.8, status: 'Open', details: 'The login form is vulnerable to SQL injection attacks.', recommendation: 'Use parameterized queries.' },
  { id: 2, title: 'Cross-Site Scripting (XSS) in Search', severity: 'High', cvss_score: 7.5, status: 'Open', details: 'Reflected XSS found in the search parameter.', recommendation: 'Sanitize all user input.' },
  { id: 3, title: 'Missing Rate Limiting', severity: 'Medium', cvss_score: 5.3, status: 'Open', details: 'No rate limiting on authentication endpoints.', recommendation: 'Implement rate limiting.' },
  { id: 4, title: 'Information Disclosure in Headers', severity: 'Low', cvss_score: 3.1, status: 'Open', details: 'Server version exposed in HTTP headers.', recommendation: 'Remove server version headers.' },
  { id: 5, title: 'Cookie Without Secure Flag', severity: 'Info', cvss_score: 0, status: 'Open', details: 'Session cookie missing Secure flag.', recommendation: 'Add Secure flag to cookies.' },
];

// Per-finding tokens for a custom summary column, resolved against the sample
// row so the preview shows the column actually varying down the table. Report
// variables like %CLIENTNAME% stay as tokens — the preview shows those raw
// everywhere else, and there is no engagement here to resolve them against.
const PREVIEW_ROW_VARS = {
  FINDINGNUMBER: (f, i) => String(i + 1),
  FINDINGTITLE: f => f.title || '',
  FINDINGSEVERITY: f => f.severity || '',
  FINDINGCVSS: f => (f.cvss_score != null ? String(f.cvss_score) : ''),
  FINDINGSTATUS: f => f.status || '',
  FINDINGUID: (f, i) => `2026-10000${i + 1}`,
  FINDINGEXPLOITABLE: () => 'No',
  FINDINGOWNER: () => 'Unassigned',
  FINDINGENDPOINTS: () => 'https://app.example.com',
  FINDINGORIGINALSEVERITY: f => f.original_severity || f.severity || '',
  FINDINGORIGINALSTATUS: f => f.original_status || f.status || '',
  FINDINGNEWSTATUS: f => f.retest_status || f.status || '',
};

// Mirror the renderers' table width/placement so narrowing a table is visible
// here rather than only in the exported file.
function tableStyleFor(config) {
  const pct = Math.min(100, Math.max(20, Number(config?.tableWidth) || 100));
  const align = config?.tableAlign || 'left';
  const font = config?.tableStyle && config.tableStyle.font;
  return {
    ...(font ? { fontFamily: cssFontFor(font) } : {}),
    width: pct + '%',
    borderCollapse: 'collapse',
    fontSize: 10,
    marginLeft: align === 'center' ? 'auto' : align === 'right' ? 'auto' : 0,
    marginRight: align === 'center' ? 'auto' : align === 'right' ? 0 : 'auto',
  };
}

function previewRowValue(value, finding, index) {
  if (!value) return ' ';   // a named blank column still needs a ruled cell
  return String(value).replace(/%([A-Z_]+)%/gi, (match, name) => {
    const fn = PREVIEW_ROW_VARS[name.toUpperCase()];
    return fn ? fn(finding, index) : match;
  });
}

const SAMPLE_SCOPE = [
  { scope_type: 'Web Application', value: 'https://app.example.com', description: 'Main application' },
  { scope_type: 'API', value: 'https://api.example.com', description: 'REST API' },
  { scope_type: 'Network', value: '10.0.0.0/24', description: 'Internal network range' },
];

const SEVERITY_COLORS = { Critical: '#7f1d1d', High: '#dc2626', Medium: '#f97316', Low: '#eab308', Info: '#3b82f6' };
const SAMPLE_SEVERITY_COUNTS = { Critical: 1, High: 1, Medium: 1, Low: 1, Info: 1 };

/** Shared renderer for component-level previews (used by both standalone sections and composite sub-components) */
/**
 * The walkthrough's Next steps block, as the preview draws it.
 *
 * Mirrors renderNextSteps in backend/routes/reports.js and the same block in
 * backend/utils/docx-report.js. Returns null on the same condition the
 * renderers use -- no `nextSteps` key means no block -- so the preview never
 * shows a band the document will not print.
 */
function NextStepsPreview({ config }) {
  const ns = config && config.nextSteps;
  if (!ns || typeof ns !== 'object') return null;
  // The same tolerant off test nextStepsConfig() uses, so an imported
  // template carrying enabled:"false" reads as off here too.
  if (ns.enabled !== undefined && (!ns.enabled || String(ns.enabled).toLowerCase() === 'false')) return null;

  const panels = (Array.isArray(ns.panels) ? ns.panels : NEXT_STEPS_PANELS)
    .filter(p => p && typeof p === 'object')
    .filter(p => String(p.title || '').trim() || String(p.body || '').trim());
  if (!panels.length) return null;

  const d = NEXT_STEPS_DEFAULTS;
  const val = (key) => (ns[key] === undefined || ns[key] === null || ns[key] === '' ? d[key] : ns[key]);
  // Sizes resolve exactly as nextStepsConfig() does on the backend: anything
  // that is not a positive finite number falls back to the default. val()
  // alone would pass a stored 0 straight through, and the preview would draw
  // text at zero point while both documents drew it at nine.
  const size = (key) => { const n = Number(ns[key]); return Number.isFinite(n) && n > 0 ? n : d[key]; };
  // Panels with gap columns between them, so they read as separate boxes
  // rather than one banded strip -- the same columns both documents build.
  // Not size(): that treats 0 as "unset" and falls back to the default,
  // and 0 is exactly how an author says they want the panels to touch.
  // Resolved the way nextStepsConfig resolves it on the backend.
  const gap = Number.isFinite(Number(ns.panelGap))
    ? Math.max(0, Math.min(10, Math.round(Number(ns.panelGap))))
    : d.panelGap;
  const cols = nextStepsColumns(panels.length, gap);
  // null is "not set", exactly as undefined is -- the documents would
  // otherwise print the word "null" where this showed nothing.
  const caption = ns.caption === undefined || ns.caption === null ? d.caption : String(ns.caption);
  const border = ns.showBorder !== false ? `1px solid ${val('borderColor')}` : 'none';
  const pad = '4px 6px';

  return (
    <div style={{ marginTop: 8 }}>
      {caption ? (
        <div style={{ color: val('captionColor'), fontSize: size('captionSize'),
                      fontWeight: ns.captionBold !== false ? 700 : 400, marginBottom: 4 }}>
          {ns.captionCaps === true ? String(caption).toUpperCase() : caption}
        </div>
      ) : null}
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <tbody>
          <tr>
            {cols.map((c, i) => (c.gap
              ? <td key={i} style={{ width: `${c.width}%` }} />
              : <td key={i} style={{ width: `${c.width}%`, border, padding: pad, verticalAlign: 'top',
                                     background: val('panelBg'), color: val('titleColor'),
                                     fontSize: size('titleSize'), whiteSpace: 'pre-line',
                                     fontWeight: ns.titleBold !== false ? 700 : 400 }}>{panels[c.panel].title}</td>
            ))}
          </tr>
          <tr>
            {/* whiteSpace pre-line, because both documents turn a typed
                newline into a real break and HTML would otherwise collapse
                it to a space. */}
            {cols.map((c, i) => (c.gap
              ? <td key={i} style={{ width: `${c.width}%` }} />
              : <td key={i} style={{ width: `${c.width}%`, border, padding: pad, verticalAlign: 'top',
                                     background: val('panelBg'), color: val('bodyColor'),
                                     fontSize: size('bodySize'), whiteSpace: 'pre-line' }}>{panels[c.panel].body}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function renderComponentPreview(type, config, styles) {
  const headingStyle = { fontSize: styles?.headingSize || 20, fontWeight: 700, marginBottom: 8, color: '#1e293b', textAlign: config.headingAlign || 'left' };
  const bodyStyle = { fontSize: styles?.bodySize || 11, lineHeight: 1.6, color: '#374151', textAlign: styles?.bodyAlignment || 'justify' };

  switch (type) {
    case 'heading': {
      const sizes = { 1: 24, 2: 20, 3: 16 };
      const sz = sizes[config.level] || 20;
      const hAlign = config.align || 'left';
      // The colour applies to both branches, as it does in both documents.
      // On the rich-text branch it is the base the block starts in, which
      // inline colour inside the heading still overrides.
      //
      // Validated the same way both renderers validate it. A nonsense value
      // is dropped by CSS too, but silently and to whatever the parent
      // happens to be, where the documents fall back to their own default --
      // so without this the three disagree on exactly the input that is
      // hardest to notice.
      const HEADING_HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
      const hColor = HEADING_HEX.test(String(config.color || '')) ? config.color : '#1e293b';
      const hStyle = { fontSize: sz, fontWeight: 700, color: hColor, marginBottom: 4, textAlign: hAlign };
      // Once a heading has html it is a rich-text heading, EMPTY OR NOT.
      // Falling back to config.text when the html was cleared showed the
      // old wording here while both documents -- which test config.html for
      // truthiness, and get no paragraphs back from an empty one -- printed
      // nothing at all.
      if (config.html) {
        const hasHtmlContent = config.html.replace(/<[^>]*>/g, '').trim();
        return hasHtmlContent
          ? <div style={hStyle} dangerouslySetInnerHTML={{ __html: normaliseAuthoredLists(config.html) }} />
          : null;
      }
      return <div style={hStyle}>{config.text || 'Section Heading'}</div>;
    }

    case 'text-block': {
      const tbStyle = {
        ...bodyStyle,
        ...(config.fontSize ? { fontSize: config.fontSize } : {}),
        ...(config.font ? { fontFamily: cssFontFor(config.font) } : {}),
        // Padded, because the generated document pads the shaded band too --
        // text sitting flush against the edge of its background previews as
        // something the output will not produce.
        ...(config.backgroundColor ? { background: config.backgroundColor, padding: '6px 8px' } : {}),
      };
      return <div style={tbStyle} dangerouslySetInnerHTML={{ __html: normaliseAuthoredLists(config.html) || '<p style="color:#9ca3af;font-style:italic">No content yet...</p>' }} />;
    }

    case 'spacer':
      return (
        <div
          title={`${config.height ?? 20}pt of white space`}
          style={{
            height: config.height ?? 20,
            // Dashed outline only so the gap is visible while editing. It is
            // blank in the document; nothing here is drawn.
            border: '1px dashed var(--border)',
            borderRadius: 3,
            opacity: 0.5,
          }}
        />
      );

    case 'version-history': {
      const ts = config.tableStyle || {};
      const headerBg = ts.headerBg || '#f3f4f6';
      const headerTextColor = ts.headerTextColor || '#000000';
      const borderColor = ts.borderColor || '#e5e7eb';
      // The real table is built at generation time from what has actually been
      // issued, which the editor has no way to know. These rows are a shape,
      // and say so, rather than a silent invention of history.
      const cols = config.allTemplates
        ? ['Version', 'Report', 'Date', 'Generated by']
        : ['Version', 'Date', 'Generated by'];
      const sample = config.allTemplates
        ? [['2', 'Standard Report', 'January 15, 2026', 'A. Tester'],
           ['1', 'Redacted Report', 'January 9, 2026', 'A. Tester'],
           ['1', 'Standard Report', 'January 4, 2026', 'A. Tester']]
        : [['2', 'January 15, 2026', 'A. Tester'],
           ['1', 'January 4, 2026', 'A. Tester']];
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          <table style={{ ...tableStyleFor(config), border: `1px solid ${borderColor}` }}>
            {ts.showHeader !== false && (
              <thead>
                <tr style={{ background: headerBg }}>
                  {cols.map((c, ci) => (
                    <th key={ci} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600,
                                          color: headerTextColor, border: `1px solid ${borderColor}` }}>{c}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {sample.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: '4px 8px', border: `1px solid ${borderColor}` }}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
            Example rows. The report lists the versions actually issued.
          </div>
        </div>
      );
    }

    case 'finding-cards': {
      // One sample finding, filled in every field the card can print, so an
      // author sees the whole layout rather than a card with half its rows
      // hidden. The grouping, pairing and field lookups are the renderers'
      // own, so what is drawn here is what the document will print.
      const SAMPLE = [{
        finding_uid: 'TSR 2026-100015',
        title: 'Unrestricted File Upload Leading to Stored HTML/Script Execution',
        severity: 'Medium', cvss_score: 6.5,
        cvss_tags: 'CVSS:3.1/AV:N/AC:L/PR:L/UI:R/S:C/C:L/I:L/A:L',
        status: 'Open', cwe: 'CWE-434 Unrestricted Upload of File with Dangerous Type',
        owasp_category: 'A04:2021', direct_exploitability: 'Yes',
        remediation_effort: 'Medium',
        affected_endpoints: 'https://app.example.com/attachments/upload',
        details: 'The attachment function accepts files of any type without validating the extension, MIME type or content.',
        observation: 'An authenticated user with upload rights can use the platform as a hosting service for active content.',
        steps_to_reproduce: JSON.stringify([
          { description: 'Authenticate to the application with a standard user account.' },
          { description: 'Upload an HTML file containing a benign proof-of-concept payload.' },
        ]),
        recommendation: 'Enforce a server-side allowlist of permitted extensions and MIME types.',
        references: 'https://cheatsheetseries.owasp.org/cheatsheets/File_Upload_Cheat_Sheet.html',
      }];
      const { metaRows, bodySections, hideEmpty, emptyText, figurePrefix } = findingCardConfig(config);
      const cards = findingCardFindings(SAMPLE, config);
      // One resolver for all three renderers, so the preview cannot claim a
      // grid the document will not draw, or the other way round.
      const grid = findingCardGrid(config);
      const gridEdge = grid.show ? { border: `1px solid ${grid.color}` } : {};
      const labelCellStyle = {
        background: config.labelBg || '#f2f5f9', color: config.labelColor || '#24416d',
        fontSize: config.labelSize || 10, fontWeight: config.labelBold !== false ? 700 : 400,
        padding: '3px 6px', verticalAlign: 'top',
        width: Math.round(reportTwips(config.labelWidth, 1700, 400, 4000) / 20),
        ...gridEdge,
      };
      const valueCellStyle = {
        color: config.valueColor || '#000000', fontSize: config.valueSize || 10,
        padding: '3px 6px', verticalAlign: 'top',
        ...gridEdge,
      };
      const statusStyle = { color: config.statusColor || '#c0392b', fontWeight: 700 };
      return (
        <div>
          {config.heading && (
            <div style={{ ...headingStyle, ...(config.headingColor ? { color: config.headingColor } : {}) }}>
              {config.heading}
            </div>
          )}
          {cards.length === 0 ? (
            <div style={{ color: config.emptyColor || '#5f6b7a', fontSize: config.bodySize || 10 }}>
              {emptyText}
            </div>
          ) : cards.map(f => {
            const present = hideEmpty
              ? metaRows.filter(r => findingCardValue(f, r.field).trim() !== '')
              : metaRows;
            const uid = findingCardValue(f, 'uid');
            const headline = config.showUid === false || !uid
              ? findingCardValue(f, 'title')
              : uid + (config.titleSeparator ?? '  |  ') + findingCardValue(f, 'title');
            const severity = findingCardValue(f, 'severity');
            return (
              <div key={f.finding_uid} style={{ marginBottom: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <tbody>
                    <tr>
                      {/* The title bar and the badge sit in the SAME table as
                          the facts grid in Word, and go through the same
                          drawTable call in the PDF -- so the grid has to
                          reach them here too, or the preview understates what
                          ticking the box does. */}
                      <td colSpan={3} style={{ background: config.titleBg || '#24416d',
                                               color: config.titleColor || '#ffffff',
                                               fontSize: config.titleSize || 11,
                                               fontWeight: config.titleBold !== false ? 700 : 400,
                                               padding: '4px 6px', ...gridEdge }}>{headline}</td>
                      <td style={{ background: findingCardSeverityBg(severity, config),
                                   color: config.severityTextColor || '#ffffff',
                                   fontSize: config.titleSize || 11, fontWeight: 700,
                                   textAlign: config.severityAlign || 'center',
                                   padding: '4px 6px', width: 76, ...gridEdge }}>
                        {config.severityUpper === false ? severity : severity.toUpperCase()}
                      </td>
                    </tr>
                    {findingCardLayout(present).map((line, li) => {
                      const { left, right } = line;
                      const leftValue = findingCardValue(f, left.field);
                      if (!right) return (
                        <tr key={li}>
                          <td style={labelCellStyle}>{left.label}</td>
                          <td colSpan={3} style={{ ...valueCellStyle,
                            ...(left.field === 'status' ? statusStyle : {}) }}>{leftValue}</td>
                        </tr>
                      );
                      return (
                        <tr key={li}>
                          <td style={labelCellStyle}>{left.label}</td>
                          <td style={{ ...valueCellStyle,
                            ...(left.field === 'status' ? statusStyle : {}) }}>{leftValue}</td>
                          <td style={labelCellStyle}>{right.label}</td>
                          <td style={{ ...valueCellStyle,
                            ...(right.field === 'status' ? statusStyle : {}) }}>
                            {findingCardValue(f, right.field)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {bodySections.map((section, si) => {
                  const raw = findingCardValue(f, section.field);
                  let lines = findingCardParagraphs(raw);
                  if (section.evidence === true) {
                    let parsed = null;
                    try { parsed = JSON.parse(raw); } catch (err) { parsed = null; }
                    if (Array.isArray(parsed)) {
                      lines = parsed
                        .map((step, i) => `${i + 1}. ${String((step && step.description) || '').trim()}`)
                        .filter(line => line.replace(/^\d+\.\s*/, ''));
                    }
                  }
                  if (!lines.length && hideEmpty) return null;
                  return (
                    <div key={si} style={{ marginTop: 6 }}>
                      <div style={{ color: config.sectionLabelColor || '#1f3864',
                                    fontSize: config.sectionLabelSize || 11,
                                    fontWeight: config.sectionLabelBold !== false ? 700 : 400 }}>
                        {section.label}
                      </div>
                      {lines.map((line, i) => (
                        <div key={i} style={{ color: config.bodyColor || '#000000',
                                              fontSize: config.bodySize || 10, marginTop: 2 }}>{line}</div>
                      ))}
                      {section.evidence === true && config.showEvidence !== false && (
                        <div style={{ color: config.figureCaptionColor || '#5f6b7a',
                                      fontSize: config.figureCaptionSize || 9,
                                      fontStyle: 'italic', marginTop: 3 }}>
                          {figurePrefix} 1: screenshots attached to this finding print here
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }
    case 'owasp-findings': {
      // Sample findings, because the preview has no engagement behind it.
      // Their categories are chosen to show the three things an author is
      // adjusting here: a category with one finding, one with two, and the
      // unclassified row. The grouping itself is the renderers' own, so what
      // is on screen is what the document will print.
      const SAMPLE = [
        { finding_uid: 'TSR 2026-100015', title: 'Unrestricted File Upload',
          severity: 'Medium', owasp_category: 'A04:2021' },
        { finding_uid: 'TSR 2026-100016', title: 'Missing Content-Security-Policy Header',
          severity: 'Low', owasp_category: 'A05:2021' },
        { finding_uid: 'TSR 2026-100017', title: 'Insufficient Input Validation',
          severity: 'Low', owasp_category: 'A03:2021' },
        { finding_uid: 'TSR 2026-100018', title: 'Default Web Server Welcome Page Exposed',
          severity: 'Low', owasp_category: 'A05:2021' },
        { finding_uid: 'TSR 2026-100019', title: 'An unclassified finding', severity: 'Low' },
      ];
      const { labels, columns, rows, emptyText } = owaspFindingsConfig(SAMPLE, config);
      const border = `1px solid ${config.borderColor || '#d8dfe8'}`;
      const cell = { border, padding: '4px 6px', verticalAlign: 'top' };
      const WIDTH = { category: Math.round(reportTwips(config.categoryWidth, 3000) / 20),
                      count: Math.round(reportTwips(config.countWidth, 900) / 20) };
      const ALIGN = { category: config.categoryAlign || 'left',
                      findings: config.findingsAlign || 'left',
                      count: config.countAlign || 'left' };
      const COLOUR = { category: config.categoryColor || '#1f3864',
                       findings: config.findingsColor || '#1f3864',
                       count: config.countColor || '#1f3864' };
      const BOLD = { category: config.categoryBold === true,
                     findings: config.findingsBold === true,
                     count: config.countBold === true };
      return (
        <div>
          {config.heading && (
            <div style={{ ...headingStyle, ...(config.headingColor ? { color: config.headingColor } : {}) }}>
              {config.heading}
            </div>
          )}
          {rows.length === 0 ? (
            <div style={{ color: config.emptyColor || '#5f6b7a', fontSize: config.bodySize || 9 }}>
              {emptyText}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  {columns.map(k => (
                    <th key={k} style={{ ...cell, width: WIDTH[k] ? WIDTH[k] : 'auto',
                                         background: config.headerBg || '#1f3864',
                                         color: config.headerColor || '#ffffff',
                                         fontSize: config.headerSize || 9,
                                         fontWeight: config.headerBold !== false ? 700 : 400,
                                         textAlign: ALIGN[k] }}>{labels[k]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.code || `unclassified-${i}`}>
                    {columns.map(k => (
                      <td key={k} style={{ ...cell, textAlign: ALIGN[k], color: COLOUR[k],
                                           fontSize: config.bodySize || 9,
                                           fontWeight: BOLD[k] ? 700 : 400 }}>
                        {k === 'count' ? r.count : r[k]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    case 'findings-walkthrough': {
      // Sample rows, because the preview has no engagement behind it. Two is
      // enough to show the severity block and the description line, which are
      // the two things an author is actually adjusting here.
      const SAMPLE = [
        { finding_uid: 'TSR 2026-100015', title: 'Unrestricted File Upload Leading to Stored HTML/Script Execution',
          details: 'Any file type can be uploaded and later executed in the browser.',
          observation: 'The upload form accepts .html and .svg without server-side checks.',
          recommendation: 'Restrict uploads to an allow-list and serve them from a separate origin.',
          severity: 'Medium', cvss_score: 6.5, status: 'Open', direct_exploitability: 'Yes' },
        { finding_uid: 'TSR 2026-100016', title: 'Missing Content-Security-Policy Header',
          details: 'No Content-Security-Policy is enforced, so any client-side injection runs unrestricted.',
          observation: 'No CSP header was returned on any tested response.',
          recommendation: 'Add a Content-Security-Policy with a nonce-based script-src.',
          severity: 'Low', cvss_score: 3.7, status: 'Open', direct_exploitability: 'No' },
      ];
      const SEV = { Critical: '#7f1d1d', High: '#dc2626', Medium: '#ea580c', Low: '#ca8a04',
                    Info: '#2e86c1', ...(config.severityColors || {}) };
      const cols = walkthroughColumns(config);
      const LABEL = walkthroughLabels(config);
      // Twips to points, the same conversion the PDF renderer uses, so the
      // width control moves the preview as well as the document.
      const WIDTH = { uid: Math.round(reportTwips(config.uidWidth, 1700) / 20),
                      severity: 78, cvss: 54, exploitable: 84, status: 70 };
      // The same alignment map both renderers use, so the preview does not
      // centre a header the document left-aligns.
      const ALIGN = { uid: config.uidAlign || 'left',
                      finding: config.findingAlign || 'left', severity: config.severityAlign || 'center',
                      cvss: config.cvssAlign || 'center', exploitable: config.exploitableAlign || 'center',
                      status: config.statusAlign || 'left' };
      // Which field the second line draws from, and whether there is one at
      // all. The preview used to print f.details whatever the author picked.
      const descField = walkthroughDescriptionField(config);
      const showsDescription = walkthroughShowsDescription(config);
      const border = `1px solid ${config.borderColor || '#d8dfe8'}`;
      const cell = { border, padding: '4px 6px', verticalAlign: 'top' };
      return (
        <div>
          {config.heading && (
            <div style={{ ...headingStyle, ...(config.headingColor ? { color: config.headingColor } : {}) }}>
              {config.heading}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {cols.map(k => (
                  <th key={k} style={{ ...cell, width: WIDTH[k] ? WIDTH[k] : 'auto',
                                       background: config.headerBg || '#1f3864',
                                       color: config.headerColor || '#ffffff',
                                       fontSize: config.headerSize || 9,
                                       fontWeight: config.headerBold !== false ? 700 : 400,
                                       textAlign: ALIGN[k] }}>{LABEL[k]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SAMPLE.map(f => (
                <tr key={f.finding_uid}>
                  {cols.map(k => {
                    if (k === 'uid') return (
                      <td key={k} style={{ ...cell, textAlign: ALIGN.uid,
                                           color: config.uidColor || '#1f3864',
                                           fontSize: config.uidSize || 9,
                                           fontWeight: config.uidBold === true ? 700 : 400 }}>
                        {f.finding_uid}
                      </td>
                    );
                    if (k === 'finding') return (
                      <td key={k} style={cell}>
                        <div style={{ color: config.titleColor || '#1f3864', fontSize: config.titleSize || 9, fontWeight: 700 }}>
                          {(walkthroughInlineUid(config) ? f.finding_uid + (config.uidSeparator ?? '  |  ') : '') + f.title}
                        </div>
                        {showsDescription && (
                          <div style={{ color: config.descriptionColor || '#5f6b7a', fontSize: config.descriptionSize || 8 }}>
                            {f[descField]}
                          </div>
                        )}
                      </td>
                    );
                    if (k === 'severity') {
                      const text = config.severityUpper === false ? f.severity : f.severity.toUpperCase();
                      const asBlock = config.severityStyle !== 'text';
                      return (
                        <td key={k} style={{ ...cell, textAlign: 'center',
                                             background: asBlock ? (SEV[f.severity] || '#6b7280') : 'transparent',
                                             color: asBlock ? (config.severityTextColor || '#ffffff') : (SEV[f.severity] || '#000'),
                                             fontSize: config.severitySize || 8, fontWeight: 700 }}>{text}</td>
                      );
                    }
                    if (k === 'cvss') return (
                      <td key={k} style={{ ...cell, textAlign: 'center', color: config.cvssColor || '#1f3864',
                                           fontSize: config.cvssSize || 9 }}>{f.cvss_score}</td>
                    );
                    if (k === 'exploitable') return (
                      <td key={k} style={{ ...cell, textAlign: ALIGN.exploitable,
                                           color: config.exploitableColor || '#1f3864',
                                           fontSize: config.exploitableSize || 9,
                                           fontWeight: config.exploitableBold === true ? 700 : 400 }}>
                        {f.direct_exploitability || 'No'}
                      </td>
                    );
                    return (
                      <td key={k} style={{ ...cell, color: config.statusColor || '#c00000',
                                           fontSize: config.statusSize || 9,
                                           fontWeight: config.statusBold !== false ? 700 : 400 }}>{f.status}</td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>
            Sample rows. The report uses this engagement&rsquo;s own findings.
          </div>
          <NextStepsPreview config={config} />
        </div>
      );
    }

    case 'revalidation-summary': {
      // Sample rows, because the preview has no engagement behind it. The
      // third row carries no snapshot and the fourth was closed before the
      // retest, so the row rules an author is adjusting here -- which findings
      // appear at all -- are visible rather than described.
      const SAMPLE = [
        { finding_uid: 'TSR 2026-100015', title: 'Unrestricted File Upload Leading to Stored Script Execution',
          severity: 'Low', status: 'Closed',
          original_severity: 'Critical', original_status: 'Open', retest_status: 'Closed' },
        { finding_uid: 'TSR 2026-100016', title: 'Missing Content-Security-Policy Header',
          severity: 'Low', status: 'Reopened',
          original_severity: 'Medium', original_status: 'Open', retest_status: 'Reopened' },
        { finding_uid: 'TSR 2026-100021', title: 'Verbose Error Messages',
          severity: 'Low', status: 'Accepted',
          original_severity: 'Low', original_status: 'Open', retest_status: 'Accepted' },
        { finding_uid: 'TSR 2026-100099', title: 'Cookie Without Secure Flag (closed before the retest)',
          severity: 'Info', status: 'Closed',
          original_severity: 'Info', original_status: 'Closed', retest_status: 'Closed' },
      ];
      const SEV = { Critical: '#7d1d1d', High: '#dc2626', Medium: '#f97316', Low: '#eab308',
                    Info: '#3b82f6', ...(config.severityColors || {}) };
      const STAT = { ...REVALIDATION_STATUS_COLORS, ...(config.statusColors || {}) };
      const cols = revalidationColumns(config);
      const LABEL = revalidationLabels(config);
      const rows = revalidationRows(SAMPLE, config);
      // Twips to points, the same conversion the PDF renderer uses, so the
      // width control moves the preview as well as the document.
      const WIDTH = { num: Math.round(reportTwips(config.numWidth, 620) / 20),
                      uid: Math.round(reportTwips(config.uidWidth, 1700) / 20),
                      originalRisk: Math.round(reportTwips(config.originalRiskWidth, 1500) / 20),
                      originalStatus: Math.round(reportTwips(config.originalStatusWidth, 1700) / 20),
                      newStatus: Math.round(reportTwips(config.newStatusWidth, 1700) / 20) };
      const ALIGN = { num: config.numAlign || 'center', uid: config.uidAlign || 'left',
                      title: config.titleAlign || 'left',
                      originalRisk: config.originalRiskAlign || 'center',
                      originalStatus: config.originalStatusAlign || 'center',
                      newStatus: config.newStatusAlign || 'center' };
      const headerBg = config.headerBg || '#24416d';
      const valueSize = Number(config.valueSize) || 8;
      const cell = (k, text) => {
        const base = { padding: '4px 6px', textAlign: ALIGN[k],
                       border: `1px solid ${config.borderColor || '#d8dfe8'}`,
                       ...(WIDTH[k] ? { width: WIDTH[k] } : {}) };
        if (k === 'title') {
          return <td key={k} style={{ ...base, fontSize: Number(config.titleSize) || 9,
                                      fontWeight: config.titleBold ? 700 : 400 }}>{text}</td>;
        }
        if (k === 'num' || k === 'uid') {
          return <td key={k} style={{ ...base, fontSize: valueSize }}>{text}</td>;
        }
        const palette = k === 'originalRisk' ? SEV : STAT;
        const upper = k === 'originalRisk' ? config.severityUpper !== false : config.statusUpper !== false;
        const style = k === 'originalRisk' ? config.severityStyle : config.statusStyle;
        const shown = upper ? String(text).toUpperCase() : text;
        if (style === 'fill') {
          return (
            <td key={k} style={{ ...base, fontSize: valueSize, fontWeight: 700,
                                 background: palette[text] || '#6b7280',
                                 color: config.valueTextColor || '#ffffff' }}>{shown}</td>
          );
        }
        return (
          <td key={k} style={{ ...base, fontSize: valueSize, fontWeight: 700,
                               color: palette[text] || '#000000' }}>{shown}</td>
        );
      };
      return (
        <div>
          {config.heading && (
            <div style={{ fontSize: styles?.headingSize || 20, fontWeight: 700, marginBottom: 6,
                          color: config.headingColor || styles?.headingColor || '#1f3864',
                          textAlign: config.headingAlign || 'left' }}>{config.heading}</div>
          )}
          {rows.length === 0 ? (
            <div style={{ fontSize: 11, fontStyle: 'italic', color: config.emptyTextColor || '#5f6b7a' }}>
              {config.emptyText || 'No findings were revalidated.'}
            </div>
          ) : (
            <table style={{ ...tableStyleFor(config), borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  {cols.map(k => (
                    <th key={k} style={{
                      padding: '4px 6px', textAlign: ALIGN[k], background: headerBg,
                      color: config.headerColor || '#ffffff',
                      fontWeight: config.headerBold !== false ? 700 : 400,
                      fontSize: Number(config.headerSize) || 9,
                      border: `1px solid ${config.borderColor || '#d8dfe8'}`,
                      ...(WIDTH[k] ? { width: WIDTH[k] } : {}),
                    }}>{LABEL[k]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((f, i) => (
                  <tr key={f.finding_uid}>
                    {cols.map(k => cell(k, k === 'num' ? String(i + 1) : revalidationValue(f, k)))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>
            Sample rows. The report uses this engagement&rsquo;s own retested findings.
          </div>
        </div>
      );
    }

    case 'metric-tiles': {
      const tiles = Array.isArray(config.tiles) ? config.tiles.filter(Boolean) : [];
      if (tiles.length === 0) {
        return <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>No tiles yet.</div>;
      }
      const perRow = Math.max(1, Number(config.perRow) || tiles.length);
      const rows = [];
      for (let i = 0; i < tiles.length; i += perRow) rows.push(tiles.slice(i, i + perRow));
      const upper = config.upperCaseLabels !== false;
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          {rows.map((row, ri) => (
            <div key={ri} style={{ display: 'flex', gap: 4, marginBottom: 4, ...tableStyleFor(config) }}>
              {row.map((t, ti) => (
                <div key={ti} style={{
                  flex: 1, minWidth: 0, padding: '6px 4px', borderRadius: 2,
                  background: t.bg || 'transparent',
                  textAlign: metricTileAlign(t, config),
                  display: 'flex', flexDirection: config.labelPosition === 'bottom' ? 'column-reverse' : 'column',
                  // The caption-to-figure gap, from the same resolver the two
                  // document renderers use -- less the half-leading the two
                  // line boxes already contribute, because CSS puts that
                  // inside the boxes and `gap` would stack on top of it. Word
                  // and PDFKit add their spacing below a line box too, so
                  // subtracting here is what makes the same number look the
                  // same in all three. The row's own `gap: 4` above is the
                  // gutter BETWEEN tiles -- a different measurement.
                  gap: Math.max(0, metricTileLabelGap(config)
                    - ((config.labelSize || 8) * 0.1 + (config.valueSize || 16) * 0.075)),
                }}>
                  <div style={{ fontSize: (config.labelSize || 8), fontWeight: 700, lineHeight: 1.2,
                                textTransform: upper ? 'uppercase' : 'none',
                                color: t.labelColor || '#666666' }}>
                    {t.label || ' '}
                  </div>
                  <div style={{ fontSize: (config.valueSize || 16), fontWeight: 700, lineHeight: 1.15,
                                color: t.valueColor || '#000000', overflowWrap: 'anywhere' }}>
                    {t.value || ' '}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    case 'custom-table': {
      const cols = config.columns || [];
      const rows = config.rows || [];
      const rStyles = config.rowStyles || [];
      const cStyles = config.cellStyles || [];
      // Column styles were not read here at all, so a column set to centre
      // previewed left while the Word renderer centred it.
      const colStyles = config.columnStyles || [];
      const ts = config.tableStyle || {};
      const headerBg = ts.headerBg || '#f3f4f6';
      const headerTextColor = ts.headerTextColor || '#000000';
      const borderColor = ts.borderColor || '#e5e7eb';
      const borderStyle = ts.borderStyle || 'full';
      const getCellBorder = (isLast) => {
        if (borderStyle === 'none') return 'none';
        if (borderStyle === 'horizontal') return isLast ? 'none' : `1px solid ${borderColor}`;
        return `1px solid ${borderColor}`; // full
      };
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          <table style={{ ...tableStyleFor(config), border: borderStyle === 'full' ? `1px solid ${borderColor}` : 'none' }}>
            {/* Absent means shown, so templates predating the toggle are
                unchanged. The preview has to honour it or it would show a
                header the generated document does not have, which is the one
                thing a preview must never do. */}
            {ts.showHeader !== false && (
              <thead>
                <tr style={{ background: headerBg }}>
                  {cols.map((c, ci) => (
                    <th key={ci} style={{
                      padding: '4px 8px', textAlign: 'left', color: headerTextColor, fontWeight: 600,
                      borderBottom: `1px solid ${borderColor}`,
                      borderRight: borderStyle === 'full' && ci < cols.length - 1 ? `1px solid ${borderColor}` : 'none'
                    }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, ri) => {
                const rs = rStyles[ri] || {};
                const isStriped = ts.stripedRows && ri % 2 === 1;
                const rowBg = rs.bg || (isStriped ? (ts.stripedColor || '#f9fafb') : undefined);
                const textColor = rs.textColor || undefined;
                // A set height is a minimum in both renderers, and CSS
                // `height` on a table row behaves the same way.
                return (
                  <tr key={ri} style={{ background: rowBg, height: rs.height ? `${rs.height}pt` : undefined }}>
                    {(Array.isArray(row) ? row : []).map((cell, ci) => {
                      const cs = (cStyles[ri] && cStyles[ri][ci]) || {};
                      // Cell, then row, then column -- the renderers' order.
                      // Content carrying its own alignment still wins, because
                      // it is rendered as HTML inside this cell.
                      const cellAlign = cs.align || rs.align || (colStyles[ci] || {}).align;
                      return (
                        <td key={ci} style={{
                          padding: '3px 8px',
                          textAlign: cellAlign || undefined,
                          color: cs.textColor || textColor,
                          background: cs.bg || undefined,
                          borderBottom: getCellBorder(ri === rows.length - 1),
                          borderRight: borderStyle === 'full' && ci < cols.length - 1 ? `1px solid ${borderColor}` : 'none'
                        }} dangerouslySetInnerHTML={{ __html: cell || '' }} />
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    case 'image': {
      const imgAlign = config.align || 'center';
      const imgTextAlign = imgAlign;
      const imgMargin = imgAlign === 'center' ? '0 auto' : imgAlign === 'right' ? '0 0 0 auto' : '0';
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          {config.imageData ? (
            <div style={{ textAlign: imgTextAlign }}>
              {/* Pixel dimensions when the template carries them, scaled to
                  the preview's narrower page. Falling back to the old
                  percentage keeps templates saved before the W/H boxes
                  looking as they always have. The preview must agree with
                  docx-report.js renderImage: a preview that keeps the browser's
                  own proportions while Word is handed different ones is how the
                  squashed-image bug stayed invisible. */}
              <img src={config.imageData} alt={config.caption || 'Figure'} style={(() => {
                const base = { borderRadius: 4, border: '1px solid #e5e7eb', display: 'block', margin: imgMargin };
                // Resolved the same way the editor and renderImage resolve them,
                // so all three agree. Gating on the width alone was wrong: typing
                // only a height on a template saved before these boxes left the
                // preview on the percentage path, showing proportions Word would
                // not produce -- which is precisely how the original squashing
                // went unnoticed.
                const pxW = Number(config.imageWidthPx) || 0;
                const pxH = Number(config.imageHeightPx) || 0;
                if (pxW <= 0 && pxH <= 0) return { ...base, maxWidth: `${config.width || 80}%` };
                const natW = Number(config.imageNaturalWidth) || 0;
                const natH = Number(config.imageNaturalHeight) || 0;
                const ratio = (natW > 0 && natH > 0) ? natH / natW : 0.75;
                const w = Math.min(600, Math.max(10, pxW || Math.round((pxH || 1) / ratio)));
                const h = Math.min(900, Math.max(1, pxH || Math.round(w * ratio)));
                return { ...base, width: `${Math.min(100, (w / 600) * 100)}%`,
                         aspectRatio: `${w} / ${h}`, objectFit: 'fill' };
              })()} />
              {config.caption && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>{config.caption}</div>}
            </div>
          ) : (
            <div style={{
              width: `${config.width || 80}%`, height: 80, margin: imgMargin,
              background: '#f3f4f6', borderRadius: 4, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#9ca3af', fontSize: 10, border: '1px dashed #d1d5db'
            }}>[Image placeholder]</div>
          )}
        </div>
      );
    }

    case 'severity-chart': {
      const counts = SAMPLE_SEVERITY_COUNTS;
      const total = Object.values(counts).reduce((s, v) => s + v, 0);
      if (config.chartType === 'bar') {
        const maxCount = Math.max(...Object.values(counts), 1);
        return (
          <div>
            {config.heading && <div style={headingStyle}>{config.heading}</div>}
            {Object.entries(counts).map(([sev, cnt]) => (
              <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 50, fontSize: 10, fontWeight: 600, textAlign: 'right', color: SEVERITY_COLORS[sev] }}>{sev}</div>
                <div style={{ flex: 1, height: 16, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${(cnt / maxCount) * 100}%`, height: '100%', background: SEVERITY_COLORS[sev], borderRadius: 3 }} />
                </div>
                <div style={{ width: 20, fontSize: 10, fontWeight: 600, color: '#374151' }}>{cnt}</div>
              </div>
            ))}
          </div>
        );
      }
      // Pie chart (default)
      let gradientParts = [];
      let pct = 0;
      Object.entries(counts).forEach(([sev, cnt]) => {
        const slice = total > 0 ? (cnt / total) * 100 : 0;
        gradientParts.push(`${SEVERITY_COLORS[sev]} ${pct}% ${pct + slice}%`);
        pct += slice;
      });
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: `conic-gradient(${gradientParts.join(', ')})`
            }} />
            <div>
              {Object.entries(counts).map(([sev, cnt]) => (
                <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, marginBottom: 2 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: SEVERITY_COLORS[sev] }} />
                  <span style={{ color: SEVERITY_COLORS[sev], fontWeight: 600 }}>{sev}:</span>
                  <span>{cnt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    case 'horizontal-line': {
      const lineW = config.width || 100;
      return (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
          <div style={{ borderTop: `${config.thickness || 1}px solid ${config.color || '#cccccc'}`, width: `${lineW}%` }} />
        </div>
      );
    }

    default:
      return <div style={{ color: '#9ca3af', fontSize: 10 }}>Unknown component: {type}</div>;
  }
}

// Sample values for the preview. Real generation substitutes the engagement's
// own data; these exist so the cover reads like a cover rather than a row of
// %TOKENS%.
const PREVIEW_VARS = {
  CLIENTNAME: 'Acme Corporation',
  ENGAGEMENTTYPE: 'Web Application Penetration Test',
  STARTDATE: '2024-01-15',
  ENDDATE: '2024-01-22',
  TESTERNAME: 'John Smith',
  REPORTDATE: '2024-01-25',
  REPORTVERSION: '1',
  ENGAGEMENTNAME: 'Acme Q1 Assessment',
  ENGAGEMENTSEVERITY: 'Critical',
  VENDORNAME: 'Acme Corporation',
};

const TITLE_PAGE_PREVIEW = [
  { key: 'title',        show: 'showTitle',          text: 'titleText',          fallback: 'Penetration Testing Report',
    style: { fontSize: 24, fontWeight: 700, marginBottom: 12 }, primary: true },
  { key: 'client',       show: 'showClient',         text: 'clientText',         fallback: '%CLIENTNAME%',
    style: { fontSize: 16, color: '#6b7280', marginBottom: 8 } },
  { key: 'type',         show: 'showEngagementType', text: 'engagementTypeText', fallback: '%ENGAGEMENTTYPE%',
    style: { fontSize: 12, color: '#6b7280', marginBottom: 8 } },
  { key: 'date',         show: 'showDate',           text: 'dateText',           fallback: 'Date: %STARTDATE% - %ENDDATE%',
    style: { fontSize: 12, color: '#9ca3af' } },
  { key: 'tester',       show: 'showTester',         text: 'testerText',         fallback: 'Tester: %TESTERNAME%',
    style: { fontSize: 12, color: '#9ca3af' } },
  { key: 'confidential', show: 'showConfidential',   text: 'confidentialText',   fallback: 'CONFIDENTIAL',
    style: { fontSize: 10, color: '#9ca3af', marginTop: 16 } },
];

function titlePagePreviewLines(config, styles) {
  const cfg = config || {};
  const out = [];
  for (const def of TITLE_PAGE_PREVIEW) {
    if (cfg[def.show] === false) continue;
    const custom = cfg[def.text];
    const raw = (typeof custom === 'string' && custom.trim() !== '') ? custom : def.fallback;
    const resolved = raw.replace(/%([A-Za-z_]+)%/g, (m, name) => {
      const v = PREVIEW_VARS[name.toUpperCase()];
      return v === undefined ? m : v;
    });
    const norm = t => String(t).replace(/\s+/g, ' ').trim();
    const hadVars = /%[A-Za-z_]+%/.test(raw);
    const skeleton = norm(raw.replace(/%[A-Za-z_]+%/g, ''));
    const shown = norm(resolved);
    if (shown === '') continue;
    if (hadVars && shown === skeleton) continue;
    out.push({
      key: def.key,
      text: resolved.trim(),
      style: def.primary
        ? { ...def.style, color: (styles && styles.primaryColor) || '#3b82f6' }
        : def.style,
    });
  }
  return out;
}

function renderSectionPreview(section, styles, allSections) {
  const config = section.config || {};
  const headingStyle = { fontSize: styles?.headingSize || 20, fontWeight: 700, marginBottom: 8, color: '#1e293b', textAlign: config.headingAlign || 'left' };
  const bodyStyle = { fontSize: styles?.bodySize || 11, lineHeight: 1.6, color: '#374151', textAlign: styles?.bodyAlignment || 'justify' };

  switch (section.type) {
    // New standalone component types delegate to shared renderer
    case 'heading':
    case 'text-block':
    case 'custom-table':
    case 'image':
    case 'severity-chart':
    case 'horizontal-line':
    case 'spacer':
    case 'version-history':
    case 'metric-tiles':
    case 'findings-walkthrough':
    case 'owasp-findings':
    case 'finding-cards':
    case 'revalidation-summary':
      return renderComponentPreview(section.type, config, styles);

    case 'composite':
      return (
        <div>
          {/* Spread onto headingStyle rather than changing the constant, so
              the nine other section types that share it keep their colour. */}
          {config.heading && (
            <div style={{ ...headingStyle, ...(config.headingColor ? { color: config.headingColor } : {}) }}>
              {config.heading}
            </div>
          )}
          {(config.components || []).length > 0 ? (
            (config.components || []).map(comp => (
              <div key={comp.id} style={{ marginBottom: 8 }}>
                {renderComponentPreview(comp.type, comp.config || {}, styles)}
              </div>
            ))
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 10, fontStyle: 'italic' }}>Add components to this section</div>
          )}
        </div>
      );
    case 'title-page':
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          {config.showLogo !== false && (
            <div style={{
              width: 80, height: 80, margin: '0 auto 16px', background: '#f3f4f6',
              borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#9ca3af', fontSize: 10
            }}>[Logo]</div>
          )}
          {/* Mirrors titlePageLines in backend/utils/report-helpers.js, against
              sample data. Same keys, same defaults, same rule for dropping a
              line whose variables all resolve empty -- a preview that showed a
              line the document will not have would be worse than no preview. */}
          {titlePagePreviewLines(config, styles).map(line => (
            <div key={line.key} style={line.style}>{line.text}</div>
          ))}
        </div>
      );

    case 'table-of-contents': {
      // Which sections list, what they are called and how deep they sit come
      // from the shared resolver, so the preview shows the contents page the
      // document will actually have. It used to carry a third rule of its own:
      // it listed spacers and rules, numbered them, and showed a heading band
      // as the bare word "Heading".
      const entries = tocEntries(allSections || []);
      return (
        <div>
          <div style={headingStyle}>{config.heading || 'Table of Contents'}</div>
          <div style={{ ...bodyStyle, paddingLeft: 8 }}>
            {entries.length > 0 ? entries.map((e, i) => (
              <div key={e.id} style={{
                padding: '2px 0',
                paddingLeft: (e.level - 1) * 14,
                borderBottom: i < entries.length - 1 ? '1px dotted #d1d5db' : 'none'
              }}>
                {e.number != null ? `${e.number}. ` : ''}{e.label}
              </div>
            )) : (
              <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>Add sections to see table of contents</div>
            )}
          </div>
        </div>
      );
    }

    case 'rich-text': {
      const rtStyle = {
        ...bodyStyle,
        ...(config.fontSize ? { fontSize: config.fontSize } : {}),
        ...(config.font ? { fontFamily: cssFontFor(config.font) } : {}),
        ...(config.backgroundColor ? { background: config.backgroundColor, padding: '6px 8px' } : {}),
      };
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          <div style={rtStyle} dangerouslySetInnerHTML={{ __html: normaliseAuthoredLists(config.html) || '<p style="color:#9ca3af;font-style:italic">No content yet...</p>' }} />
        </div>
      );
    }

    case 'severity-dashboard':
      return (
        <div>
          <div style={headingStyle}>{config.heading || 'Dashboard'}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries({ Critical: 1, High: 1, Medium: 1, Low: 1, Info: 1 }).map(([sev, cnt]) => (
              <div key={sev} style={{
                padding: '8px 16px', borderRadius: 6,
                background: SEVERITY_COLORS[sev] + '18', color: SEVERITY_COLORS[sev],
                fontWeight: 700, fontSize: 12, textAlign: 'center', minWidth: 60
              }}>
                <div style={{ fontSize: 18 }}>{cnt}</div>
                <div>{sev}</div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'risk-rating-table': {
      // Rows and headings come from the same resolver the document renderers
      // use, and are drawn the way those renderers draw them -- a filled cell
      // with the band name reversed out of it, a numbered first column, the
      // description in italics. This preview used to invent both its wording
      // and its appearance, so an author approved a table the report never
      // printed.
      const { labels, rows } = riskRatingConfig(config);
      const cell = { padding: '3px 8px', border: '1px solid #e5e7eb' };
      const head = { ...cell, padding: '4px 8px', textAlign: 'left', fontWeight: 600 };
      return (
        <div>
          <div style={headingStyle}>{config.heading || 'Risk Rating Methodology'}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ ...head, width: 24 }}>{labels.index}</th>
                <th style={head}>{labels.rating}</th>
                <th style={head}>{labels.score}</th>
                <th style={head}>{labels.description}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td style={cell}>{idx + 1}</td>
                  <td style={{ ...cell, background: row.color, color: '#fff', fontWeight: 600, textAlign: 'center' }}>
                    {row.severity.toUpperCase()}
                  </td>
                  <td style={{ ...cell, textAlign: 'center' }}>{row.score}</td>
                  <td style={{ ...cell, fontStyle: 'italic' }}>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'scope-table': {
      // Three changes of truthfulness here, all of the same kind. The headings
      // are the template's. The heading line is drawn only when there is one,
      // because both renderers are `if (config.heading)` and this used to
      // invent the word 'Scope' for a section that would print none. And the
      // table honours the Table Width control, which it used to ignore
      // entirely while both renderers obeyed it -- so one of this section's
      // two controls changed nothing on screen.
      const { labels: scopeLabels } = scopeTableConfig(config);
      return (
        <div>
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          <table style={tableStyleFor(config)}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e5e7eb' }}>{scopeLabels.type}</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e5e7eb' }}>{scopeLabels.target}</th>
                <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e5e7eb' }}>{scopeLabels.description}</th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_SCOPE.map((s, i) => (
                <tr key={i}>
                  <td style={{ padding: '3px 8px', border: '1px solid #e5e7eb' }}>{s.scope_type}</td>
                  <td style={{ padding: '3px 8px', border: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: 9 }}>{s.value}</td>
                  <td style={{ padding: '3px 8px', border: '1px solid #e5e7eb' }}>{s.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // The preview used to hardcode four columns and ignore config.columns, so
    // toggling a column changed nothing here. It now mirrors what the renderers
    // actually emit, including any custom columns.
    case 'findings-summary': {
      const cols = findingsSummaryColumns(config);
      const custom = (config.customColumns || []).filter(c => c && typeof c === 'object');
      const sumLabels = findingsSummaryLabels(config);
      const th = { padding: '4px 8px', textAlign: 'left', border: '1px solid #e5e7eb' };
      const td = { padding: '3px 8px', border: '1px solid #e5e7eb' };
      return (
        <div>
          {/* Drawn only when there is one: both renderers are `if (config.heading)`,
              and this used to invent 'Vulnerability Summary' -- which was not even
              the default heading, 'Vulnerability Summary Table'. */}
          {config.heading && <div style={headingStyle}>{config.heading}</div>}
          <table style={tableStyleFor(config)}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                {cols.includes('#') && <th style={{ ...th, width: 30 }}>{sumLabels['#']}</th>}
                {cols.includes('title') && <th style={th}>{sumLabels.title}</th>}
                {cols.includes('severity') && <th style={{ ...th, width: 60 }}>{sumLabels.severity}</th>}
                {cols.includes('cvss') && <th style={{ ...th, width: 40 }}>{sumLabels.cvss}</th>}
                {cols.includes('directlyExploitable') && <th style={{ ...th, width: 70 }}>{sumLabels.directlyExploitable}</th>}
                {cols.includes('status') && <th style={{ ...th, width: 60 }}>{sumLabels.status}</th>}
                {custom.map((c, n) => <th key={'h' + n} style={th}>{c.label || <em style={{ color: '#9ca3af' }}>unnamed</em>}</th>)}
              </tr>
            </thead>
            <tbody>
              {SAMPLE_FINDINGS.map((f, i) => (
                <tr key={f.id}>
                  {cols.includes('#') && <td style={td}>{i + 1}</td>}
                  {cols.includes('title') && <td style={td}>{f.title}</td>}
                  {cols.includes('severity') && <td style={{ ...td, color: SEVERITY_COLORS[f.severity], fontWeight: 600 }}>{f.severity}</td>}
                  {cols.includes('cvss') && <td style={td}>{f.cvss_score}</td>}
                  {cols.includes('directlyExploitable') && <td style={td}>No</td>}
                  {cols.includes('status') && <td style={td}>{f.status}</td>}
                  {custom.map((c, n) => (
                    <td key={'c' + n} style={{ ...td, color: c.value ? undefined : '#d1d5db' }}>
                      {previewRowValue(c.value, f, i)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'findings-detail':
      return (
        <div>
          <div style={headingStyle}>{config.heading || 'Detailed Findings'}</div>
          {SAMPLE_FINDINGS.slice(0, 2).map((f, i) => (
            <div key={f.id} style={{ marginBottom: 16, padding: 10, border: '1px solid #e5e7eb', borderRadius: 4 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{i + 1}. {f.title}</div>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                fontSize: 10, fontWeight: 600, color: '#fff',
                background: SEVERITY_COLORS[f.severity], marginBottom: 6
              }}>{f.severity} ({f.cvss_score})</span>
              {(config.showFields || []).includes('details') && f.details && (
                <div style={{ fontSize: 10, marginTop: 4 }}><strong>Details:</strong> {f.details}</div>
              )}
              {(config.showFields || []).includes('recommendation') && f.recommendation && (
                <div style={{ fontSize: 10, marginTop: 2 }}><strong>Recommendation:</strong> {f.recommendation}</div>
              )}
              {config.showEvidence !== false && (
                <div style={{ fontSize: 10, marginTop: 4, color: '#9ca3af', fontStyle: 'italic' }}>[Evidence screenshots would appear here]</div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center' }}>... and {SAMPLE_FINDINGS.length - 2} more findings</div>
        </div>
      );

    case 'page-break':
      return (
        <div style={{ borderTop: '2px dashed #d1d5db', margin: '12px 0', position: 'relative' }}>
          <span style={{
            position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
            background: '#fff', padding: '0 8px', fontSize: 9, color: '#9ca3af'
          }}>PAGE BREAK</span>
        </div>
      );

    default:
      return <div style={{ color: '#9ca3af', fontSize: 11 }}>Unknown section type: {section.type}</div>;
  }
}

/**
 * One header or footer, drawn from the same zone/block layout the report uses.
 *
 * This used to be two divs reading the flat `text` and `showPageNumber`
 * fields, so everything the zone editor could express -- a logo beside three
 * centred lines, a rule, a link, anything in `blocks` -- previewed as nothing
 * at all. An author laying out a footer was working blind and finding out at
 * render time.
 */
function HeaderFooterPreview({ config, kind, logoData }) {
  const cfg = config || {};
  const { zones, blocks } = resolveHeaderFooterLayout(cfg, kind);
  const pct = headerFooterZoneWidths(cfg);
  const baseSize = cfg.fontSize || 8;

  const partStyle = (part) => ({
    fontFamily: cssFontFor(part.fontFamily || cfg.fontFamily),
    fontSize: (part.fontSize || baseSize) + 'pt',
    color: part.color || '#999999',
    fontWeight: part.bold ? 700 : 400,
    fontStyle: part.italic ? 'italic' : 'normal',
    textDecoration: part.underline ? 'underline' : 'none',
  });

  const renderPart = (part, align) => {
    if (!part || isEmptyPart(part)) return null;

    if (part.type === 'image') {
      const src = part.imageData || (part.useTemplateLogo ? logoData : null);
      if (!src) return null;
      return <img src={src} alt="" style={{ width: part.width || 60, height: part.height || 20,
                                            objectFit: 'contain', display: 'inline-block' }} />;
    }

    if (part.type === 'pageNumber') {
      return <span style={partStyle(part)}>{part.format === 'current' ? 'Page 1' : 'Page 1 of 12'}</span>;
    }

    if (part.type === 'rule') {
      return <div style={{ borderBottom: `${part.thickness || 1}px solid ${part.color || '#cccccc'}`,
                           width: '100%', height: 0 }} />;
    }

    // text — same line splitting and URL detection the renderers use, so the
    // third line of a three-line part previews as a link exactly as it prints.
    const linkColor = headerFooterLinkColor(part);
    return (
      <span style={{ ...partStyle(part), textAlign: align, display: 'block' }}>
        {headerFooterTextLines(part.text).map((runs, li) => (
          <span key={li} style={{ display: 'block' }}>
            {runs.length === 0 ? ' ' : runs.map((run, ri) => (
              run.link
                ? <a key={ri} href={run.link} target="_blank" rel="noopener noreferrer"
                     style={{ color: linkColor, textDecoration: 'underline' }}>{run.text}</a>
                : <span key={ri}>{run.text}</span>
            ))}
          </span>
        ))}
      </span>
    );
  };

  const L = renderPart(zones.left, 'left');
  const C = renderPart(zones.center, 'center');
  const R = renderPart(zones.right, 'right');
  const live = (blocks || []).filter(b => b && !isEmptyPart(b));
  if (!L && !C && !R && live.length === 0) return null;

  // minWidth 0 matters: without it an unbroken URL in the centre pushes the
  // flex basis out and the preview stops matching the document.
  const zoneBox = (node, basis, align) => (
    <div style={{ flexBasis: `${basis}%`, flexGrow: 0, flexShrink: 0, minWidth: 0,
                  textAlign: align, overflowWrap: 'break-word' }}>{node}</div>
  );

  const edge = kind === 'footer'
    ? { paddingTop: 6, marginTop: 16, borderTop: '1px solid #e5e7eb' }
    : { paddingBottom: 6, marginBottom: 12, borderBottom: '1px solid #e5e7eb' };

  return (
    <div style={{ ...edge, fontSize: baseSize + 'pt', color: '#9ca3af' }}>
      {(L || C || R) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {zoneBox(L, pct.left, 'left')}
          {zoneBox(C, pct.center, 'center')}
          {zoneBox(R, pct.right, 'right')}
        </div>
      )}
      {live.map((b, i) => (
        <div key={i} style={{ textAlign: b.align || 'left', marginTop: 3 }}>
          {renderPart(b, b.align || 'left')}
        </div>
      ))}
    </div>
  );
}

export default function TemplatePreview({ template }) {
  const sections = template?.sections || [];
  const styles = template?.styles || {};
  const header = template?.header || {};
  const footer = template?.footer || {};

  const preview = useMemo(() => (
    <div>
      {/* Header */}
      {header.enabled !== false && (
        <HeaderFooterPreview config={header} kind="header" logoData={template?.logoData} />
      )}

      {/* Sections */}
      {sections.map((section, i) => (
        <div key={section.id} style={{ marginBottom: section.type === 'page-break' ? 0 : 16 }}>
          {renderSectionPreview(section, styles, sections)}
        </div>
      ))}

      {/* Footer */}
      {footer.enabled !== false && (
        <HeaderFooterPreview config={footer} kind="footer" logoData={template?.logoData} />
      )}
    </div>
  ), [sections, styles, header, footer, template?.logoData]);

  return (
    <div className="tpl-preview" style={{
      background: '#fff', color: '#000', padding: 24, borderRadius: 4,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxHeight: 'calc(100vh - 120px)',
      overflow: 'auto', fontSize: 11, lineHeight: 1.5
    }}>
      {/* The global reset in public/index.html zeroes padding on EVERY
          element, lists included, and nothing outside the editor put it back
          -- so authored bullets previewed with no indent at all and their
          markers drawn outside the box. These two rules restore the hanging
          indent the documents print: marker at 18pt, text at 36pt, scaled to
          the preview's 11px body. Scoped to the preview so they cannot reach
          the app's own lists. */}
      <style>{`
        .tpl-preview ul, .tpl-preview ol { padding-left: 30px; margin: 4px 0; }
        .tpl-preview li { margin: 2px 0; }
        .tpl-preview li > p { margin: 0; }
      `}</style>
      {sections.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>👁️</div>
          <div style={{ fontSize: 13 }}>Preview will appear here as you add sections.</div>
        </div>
      ) : preview}
    </div>
  );
}
